import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from "@react-keycloak/web";
import { 
    Typography, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Button, Box, Chip 
} from '@mui/material';
import CommonPage from "../common/CommonPage";
import { fetchWithdrawalsWithUsers } from '../../../store/reducers/withdrawals.reducer';
import withdrawalsAPI from '../../../api/withdrawals';
import stdDate from '../../../utils/stdDate';
import { CALL_STATUS } from '../../../store/reducers/constants';
import Loading from '../../../common/loading/loading';

const ManualWithdrawalsPage = () => {
    const { keycloak } = useKeycloak();
    const dispatch = useDispatch();
    const { requests, users, status } = useSelector(state => state.withdrawals);

    useEffect(() => {
        if (keycloak.token) {
            dispatch(fetchWithdrawalsWithUsers(keycloak.token));
        }
    }, [dispatch, keycloak.token]);

    const sortedRequests = useMemo(() => {
        return [...requests].sort((a, b) => {
            if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
            if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
            return a.createdAt - b.createdAt;
        });
    }, [requests]);

    const handleAction = async (id, newStatus) => {
        const actionText = newStatus === 'COMPLETED' ? 'approve' : 'reject';
        if (window.confirm(`Are you sure you want to ${actionText} this withdrawal?`)) {
            try {
                await withdrawalsAPI.updateStatus(id, newStatus, keycloak.token);
                dispatch(fetchWithdrawalsWithUsers(keycloak.token));
            } catch (err) {
                alert("Error changing status: " + err.message);
            }
        }
    };

    const getStatusChip = (status) => {
        const colors = {
            'PENDING': 'warning',
            'COMPLETED': 'success',
            'CANCELLED': 'error',
            'CREATED': 'info',
            'REJECTED': 'error'
        };
        return (
            <Chip 
                label={status} 
                color={colors[status] || 'default'} 
                size="small" 
                sx={{ fontWeight: 'bold', minWidth: '95px' }} 
            />
        );
    };

    const cellStyle = { verticalAlign: 'middle', py: 1.5 };

    return (
        <CommonPage>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
                Manual Withdrawals
            </Typography>
            
            {status === CALL_STATUS.PENDING && requests.length === 0 ? <Loading /> : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
                    <Table size="medium">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>User (Login)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Contact Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRequests.map((req) => (
                                <TableRow key={req.id} hover>
                                    <TableCell sx={cellStyle}>
                                        {stdDate(req.createdAt / 1000)}
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                                            {users[req.userId]?.login || 'Loading...'}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        {users[req.userId]?.email || '...'}
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        {req.amount} Credits
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        {getStatusChip(req.status)}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        {req.status === 'PENDING' ? (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                <Button 
                                                    variant="contained" 
                                                    color="success" 
                                                    size="small"
                                                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                                                    onClick={() => handleAction(req.id, 'COMPLETED')}
                                                >
                                                    APPROVE
                                                </Button>
                                                <Button 
                                                    variant="outlined" 
                                                    color="error" 
                                                    size="small"
                                                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                                                    onClick={() => handleAction(req.id, 'CANCELLED')}
                                                >
                                                    REJECT
                                                </Button>
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedRequests.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        No withdrawal requests found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </CommonPage>
    );
};

export default ManualWithdrawalsPage;